import React from 'react';
import {Link,useParams} from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AreasSection from '../components/AreasSection';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import * as FiIcons from 'react-icons/fi';

const {FiHome,FiCheck,FiPhone,FiCalendar,FiStar,FiUser,FiShield,FiClock,FiTarget,FiZap,FiAward,FiHeart}=FiIcons;

const DeepCleaningPage=()=> {
  const {area}=useParams();
  const currentArea=area ? formatAreaName(area) : 'Aylesbury';

  const handleLinkClick=()=> {
    window.scrollTo({top: 0,behavior: 'smooth'});
  };

  const mainServices=[ 
    {
      title: "Kitchen Deep Clean",
      description: "Complete kitchen transformation including oven, appliances, and all surfaces.",
      highlights: ["Oven deep clean inside & out","Appliance cleaning","Cupboards inside & out","Floor & surface sanitization"]
    },
    {
      title: "Bathroom Deep Clean", 
      description: "Thorough bathroom cleaning including descaling, sanitizing, and restoration.",
      highlights: ["Complete descaling","Mirror & chrome polishing","Deep sanitization"]
    },
    {
      title: "Living Areas",
      description: "Comprehensive cleaning of all living spaces including furniture.",
      highlights: ["Furniture cleaning","Skirting boards"]
    }
  ];

  const whyChooseDeepCleaning=[
    {icon: FiZap,title: "Instant Transformation",description: "See dramatic results immediately - your home will look completely refreshed"},
    {icon: FiHeart,title: "Health Benefits",description: "Remove allergens, bacteria, and dust for better air quality"},
    {icon: FiAward,title: "Professional Results",description: "Commercial-grade equipment delivers results you can't achieve yourself"},
    {icon: FiClock,title: "Save Time",description: "What takes you days, our team completes in hours"}
  ];

  const perfectTimes=[
    "Moving In/Out","Special Occasions","Seasonal Refresh",
    "Post-Illness Recovery","Treat Yourself","New Baby Preparation"
  ];

  const reviews=[
    {
      name: "Jennifer Thompson",
      rating: 5,
      text: `Absolutely amazing transformation in ${currentArea}! My kitchen looks brand new and the bathroom sparkles. Professional service that's worth every penny!`,
      date: "1 week ago"
    },
    {
      name: "Mark Wilson", 
      rating: 5,
      text: `Had a deep clean before Christmas guests and the results were incredible. Every room was perfectly cleaned. Highly recommend!`,
      date: "2 weeks ago"
    },
    {
      name: "Sarah Davis",
      rating: 5,
      text: `Spring cleaning made easy! They cleaned areas I didn't even know needed attention. The difference is remarkable.`,
      date: "3 weeks ago"
    }
  ];

  // Enhanced SEO schema
  const deepCleaningSchema={
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `Deep Cleaning Services ${currentArea}`,
    "description": `Professional deep cleaning services in ${currentArea}. Comprehensive home refresh, spring cleaning, pre-event cleaning.`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Diamond Cleaning Aylesbury"
    },
    "areaServed": currentArea,
    "offers": {
      "@type": "Offer",
      "price": "18",
      "priceCurrency": "GBP"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={`Deep Cleaning Services ${currentArea} - Professional Home Deep Clean | Diamond Cleaning`}
        description={`Professional deep cleaning services in ${currentArea}. Comprehensive home refresh, spring cleaning, move-in/out cleaning. Transform your home. Book online or call 07878 522378`}
        keywords={`deep cleaning ${currentArea}, professional deep clean, spring cleaning ${currentArea}, home deep clean service, move in cleaning, comprehensive cleaning`}
        schema={deepCleaningSchema}
      />
      <Header currentArea={currentArea} />

      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden pt-20">
        <div className="absolute inset-0 bg-cover bg-center" 
             style={{backgroundImage: `linear-gradient(rgba(0,0,0,0.6),rgba(0,0,0,0.6)),url('/img/deep-cleaning.webp')`}} />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 inline-block">
              TRANSFORM YOUR HOME TODAY
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Deep Cleaning Services in {currentArea}
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl mx-auto">
              <strong>Go Beyond Regular Cleaning!</strong> Professional deep cleaning that tackles every corner and surface. Perfect for special occasions,moving,or when your home needs that extra sparkle.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book" 
                    className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2 shadow-xl"
                    onClick={handleLinkClick}>
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Book Deep Clean Now</span>
              </Link>
              <a href="tel:07878522378" 
                 className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2 shadow-xl">
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>Call: 07878 522378</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Deep Cleaning */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Choose Professional Deep Cleaning?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Regular cleaning maintains your home, but deep cleaning transforms it completely.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseDeepCleaning.map((benefit,index)=> (
              <div key={index} className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow">
                <SafeIcon icon={benefit.icon} className="w-16 h-16 text-blue-600 mx-auto mb-6" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What's Included in Our Deep Clean
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every area of your home gets professional attention with no shortcuts or missed spots.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {mainServices.map((service,index)=> (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 text-center">
                  {service.description}
                </p>
                <div className="space-y-3">
                  {service.highlights.map((highlight,idx)=> (
                    <div key={idx} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                      <span className="text-gray-700">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Perfect Times Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Perfect Times for a Deep Clean
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When is the right time? Here are the most popular occasions:
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {perfectTimes.map((occasion,index)=> (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow text-center">
                <h3 className="text-lg font-bold text-gray-900">{occasion}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Simple Pricing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, honest pricing based on your home size. No hidden fees.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <SafeIcon icon={FiHome} className="w-16 h-16 text-blue-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Small Home</h3>
              <p className="text-gray-600 mb-4">1-2 bedrooms</p>
              <div className="text-3xl font-bold text-blue-600 mb-2">£180 - £250</div>
              {/* <div className="text-lg text-gray-600 mb-6">£72 - £108</div> */}
            </div>
            
            <div className="bg-blue-600 rounded-2xl p-8 text-center text-white relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-gray-900 px-6 py-2 rounded-full text-sm font-bold">
                MOST POPULAR
              </div>
              <SafeIcon icon={FiHome} className="w-16 h-16 text-white mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4">Medium Home</h3>
              <p className="text-blue-100 mb-4">2-3 bedrooms</p>
              <div className="text-3xl font-bold mb-2">£280 - £350</div>
              {/* <div className="text-lg mb-6">£108 - £144</div> */}
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <SafeIcon icon={FiHome} className="w-16 h-16 text-purple-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Large Home</h3>
              <p className="text-gray-600 mb-4">4+ bedrooms</p>
              <div className="text-3xl font-bold text-purple-600 mb-2">£380 - £450</div>
              {/* <div className="text-lg text-gray-600 mb-6">£144 - £216</div> */}
            </div>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              All prices include professional equipment, supplies, and our satisfaction guarantee.
            </p>
            <Link to="/book" 
                  className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  onClick={handleLinkClick}>
              Get Custom Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Happy Customers in {currentArea}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what our customers say about their deep cleaning experience
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map((review,index)=> (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <SafeIcon icon={FiUser} className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.name}</h4>
                    <div className="flex space-x-1">
                      {[...Array(review.rating)].map((_,i)=> (
                        <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{review.text}"</p>
                <p className="text-sm text-gray-500 mt-3">{review.date}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Home?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Don't spend your weekend scrubbing. Let our professional team give you back your time while delivering amazing results.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" 
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg flex items-center justify-center space-x-2 shadow-xl"
                  onClick={handleLinkClick}>
              <SafeIcon icon={FiCalendar} className="w-5 h-5" />
              <span>Book Your Deep Clean</span>
            </Link>
            <a href="tel:07878522378" 
               className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2 border-2 border-white shadow-xl">
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>Call: 07878 522378</span>
            </a>
          </div>
        </div>
      </section>

      <AreasSection currentArea={currentArea} />
      <Footer />
    </div>
  );
};

const formatAreaName=(area)=> {
  return area
    .split('-')
    .map(word=> word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default DeepCleaningPage;